import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Injects tracking scripts (Meta Pixel, GA4, Google Ads, GTM, custom HTML)
 * managed via /admin/tracking. Runs once on app boot.
 *
 * Idempotent: each script is tagged with data-tracking-id so re-mounts won't
 * duplicate. Disabled or deleted scripts are removed on next page load.
 */

type TrackingScript = {
  id: string;
  kind: "meta_pixel" | "ga4" | "google_ads" | "gtm" | "clarity" | "custom";
  name: string;
  pixel_id: string | null;
  head_html: string | null;
  body_html: string | null;
  enabled: boolean;
  position: number;
};

function buildSnippet(s: TrackingScript): { head: string; body: string } {
  const id = (s.pixel_id || "").trim();
  switch (s.kind) {
    case "meta_pixel":
      if (!id) return { head: "", body: "" };
      return {
        head: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');`,
        body: `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1" alt="" />`,
      };
    case "ga4":
      if (!id) return { head: "", body: "" };
      return {
        head: `(function(){var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=${id}';document.head.appendChild(s);})();window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`,
        body: "",
      };
    case "google_ads":
      if (!id) return { head: "", body: "" };
      return {
        head: `(function(){var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=${id}';document.head.appendChild(s);})();window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}');`,
        body: "",
      };
    case "gtm":
      if (!id) return { head: "", body: "" };
      return {
        head: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`,
        body: `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`,
      };
    case "clarity":
      if (!/^[a-z0-9]+$/i.test(id)) return { head: "", body: "" };
      return {
        head: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${id}");`,
        body: "",
      };
    case "custom":
      return { head: s.head_html || "", body: s.body_html || "" };
  }
}

function injectInlineScript(parent: HTMLElement, code: string, id: string) {
  const el = document.createElement("script");
  el.setAttribute("data-tracking-id", id);
  el.text = code;
  parent.appendChild(el);
}

function injectHTML(parent: HTMLElement, html: string, id: string) {
  const wrapper = document.createElement("div");
  wrapper.setAttribute("data-tracking-id", id);
  wrapper.style.display = "contents";
  wrapper.innerHTML = html;
  parent.appendChild(wrapper);
}

function cleanupGlobalTrackingScripts() {
  document.querySelectorAll("[data-tracking-id]").forEach((el) => el.remove());
  document
    .querySelectorAll('script[src*="googletagmanager.com"], script[src*="connect.facebook.net"]')
    .forEach((el) => {
      if (!el.hasAttribute("data-fastblog-script-id")) el.remove();
    });
}

function isPublicBlogPage() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const isFastblogHost = [
    "localhost",
    "fastblog.com.br",
    "www.fastblog.com.br",
    "fastblog.lovable.app",
  ].includes(host);

  return window.location.pathname.startsWith("/blog/") || !isFastblogHost;
}

export default function TrackingScriptsInjector() {
  const location = useLocation();

  useEffect(() => {
    if (isPublicBlogPage()) {
      cleanupGlobalTrackingScripts();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Leitura pública controlada: a tabela não é legível por visitantes.
        const { data, error } = await supabase.rpc("get_public_tracking_scripts");
        if (cancelled || error || !data) return;

        for (const s of data as TrackingScript[]) {
          const { head, body } = buildSnippet(s);
          const tag = `ts-${s.id}`;
          if (document.querySelector(`[data-tracking-id="${tag}"]`)) continue;
          if (head) injectInlineScript(document.head, head, tag);
          if (body) injectHTML(document.body, body, `${tag}-b`);
        }
      } catch {
        /* analytics must never break the app */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
}